import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import LocalStorage from '../utils/localStorage'
import {
  DEFAULT_PLATFORM_TIMEZONE,
  formatPlatformDate,
  formatPlatformDateTime,
  getPlatformTimezone,
  setPlatformTimezone,
} from '../utils/platformDate'
import { getPlatformSettings, updatePlatformSettings } from '../services/platformSettingsService'

interface PlatformTimezoneContextValue {
  timezone: string
  loaded: boolean
  saving: boolean
  formatDate: typeof formatPlatformDate
  formatDateTime: typeof formatPlatformDateTime
  saveTimezone: (timezone: string) => Promise<void>
}

const PlatformTimezoneContext = createContext<PlatformTimezoneContextValue>({
  timezone: DEFAULT_PLATFORM_TIMEZONE,
  loaded: false,
  saving: false,
  formatDate: formatPlatformDate,
  formatDateTime: formatPlatformDateTime,
  saveTimezone: async () => {},
})

export function PlatformTimezoneProvider({ children }: { children: React.ReactNode }) {
  const [timezone, setTimezone] = useState(getPlatformTimezone())
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)

  const applyTimezone = useCallback((tz: string) => {
    setPlatformTimezone(tz)
    setTimezone(tz)
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!LocalStorage.getApiKey()) {
        applyTimezone(DEFAULT_PLATFORM_TIMEZONE)
        setLoaded(true)
        return
      }
      try {
        const settings = await getPlatformSettings()
        if (!cancelled && settings.timezone) {
          applyTimezone(settings.timezone)
        }
      } catch {
        if (!cancelled) applyTimezone(DEFAULT_PLATFORM_TIMEZONE)
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [applyTimezone])

  const saveTimezone = useCallback(
    async (newTimezone: string) => {
      setSaving(true)
      try {
        const settings = await updatePlatformSettings(newTimezone)
        applyTimezone(settings.timezone ?? newTimezone)
      } finally {
        setSaving(false)
      }
    },
    [applyTimezone],
  )

  return (
    <PlatformTimezoneContext.Provider
      value={{
        timezone,
        loaded,
        saving,
        formatDate: formatPlatformDate,
        formatDateTime: formatPlatformDateTime,
        saveTimezone,
      }}
    >
      {children}
    </PlatformTimezoneContext.Provider>
  )
}

export function usePlatformDate() {
  return useContext(PlatformTimezoneContext)
}
